import React from 'react';
import { Link } from 'react-router-dom';
import { useAppContext } from 'src/components/app-context';
import { PanelHeader, PanelTitle } from '@prisjakt/ui/panel';
import { BodyMediumText } from '@prisjakt/ui/text/variants';
import { ShopCard } from '../shop-card';
import { Grid } from 'src/components/flexible-grid';
import type { ResponseData } from '../data/use-with-elastic';
import { useElasticFetch } from '../data/use-with-elastic';
import CampaignStorePopup from '../campaign-store-popup';
import { Wrapper } from './styles';
import { useViewportContext } from 'src/components/viewport/context';
import {
  InternalNavigationScope,
  trackInternalNavigationClick,
} from 'src/globals/tracking/tracking';
import type { RouterParams } from '../../types';

const buildQuery = () => ({
  size: 0,
  aggs: {
    stores: {
      terms: {
        field: 'store.id',
        size: 20,
        order: { total_rank: 'desc' },
      },
      aggs: {
        total_rank: { sum: { field: 'rank' } },
        top_hits: {
          top_hits: {
            _source: {
              includes: [
                'id',
                'store.id',
                'store.featured',
                'store.logo',
                'store.name',
                'store.external_uri',
              ],
            },
            size: 1,
          },
        },
      },
    },
  },
});

type PopularStoresProps = {
  campaignId: number;
  dailyDeals: {
    // eslint-disable-next-line camelcase
    web_uri: {
      responsive: string;
    };
  };
  data: ResponseData;
  router: RouterParams;
};

export const PopularStores = (props: PopularStoresProps) => {
  const [currentShop, setCurrentShop] = React.useState(undefined);
  const data = useElasticFetch({
    query: buildQuery(),
    campaignId: props.campaignId,
    timeout: 1500,
  }).data;

  const { t } = useAppContext();
  const { dailyDeals, router } = props;
  const vp = useViewportContext();
  const { numberOfStores, dealCount } = vp.isDesktop
    ? { numberOfStores: 20, dealCount: 8 }
    : { numberOfStores: 4, dealCount: 4 };

  function toggleStoreCampaignItemsPopup(shop?: any) {
    setCurrentShop(shop);
  }

  return (
    <Wrapper>
      <PanelHeader style={{ flexWrap: 'wrap' }}>
        <PanelTitle>
          {t('campaigns', 'inactive-campaign.popular-stores')}
        </PanelTitle>
        <BodyMediumText
          container={Link}
          containerProps={{
            to: dailyDeals.web_uri.responsive,
            onClick: () =>
              trackInternalNavigationClick({
                scope: InternalNavigationScope.CAMPAIGN_HEADER,
                nextPage: dailyDeals.web_uri.responsive,
              }),
          }}
        >
          {t('campaigns', 'link.view-all')}
        </BodyMediumText>
      </PanelHeader>
      <Grid columnTemplate="repeat(auto-fit, minmax(min(100%/2, max(143px, 100%/5)), 1fr))">
        {data?.aggregations?.stores?.buckets &&
          data.aggregations.stores.buckets
            .slice(0, numberOfStores)
            .map((hit: any) => {
              const shop = hit.top_hits.hits.hits[0]._source.store;
              return (
                <ShopCard
                  key={hit.key}
                  name={shop.name}
                  featured={shop.featured}
                  logo={shop.logo && (shop.logo['176'] || shop.logo['96'])}
                  onClick={() => toggleStoreCampaignItemsPopup(shop)}
                />
              );
            })}
      </Grid>
      {currentShop && dailyDeals && (
        <CampaignStorePopup
          router={router}
          campaign={dailyDeals}
          shop={currentShop}
          dealCount={dealCount}
          onRequestClose={() => toggleStoreCampaignItemsPopup()}
        />
      )}
    </Wrapper>
  );
};
